import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { KnowledgeArticleEditor } from '../../components/admin/KnowledgeArticleEditor';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  BookOpen,
  Check,
  X,
  Eye,
  EyeOff,
  Bot,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface KnowledgeArticle {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  video_embed: string | null;
  category: string;
  is_published: boolean;
  view_count: number;
  created_at: string;
  updated_at: string;
  chunk_count?: number;
  walkthrough_token: string | null;
  walkthrough_title: string | null;
}

const CATEGORIES = [
  { value: 'campaigns', label: 'Campagnes' },
  { value: 'blogs', label: 'Blogs' },
  { value: 'mailinglist', label: 'Mailinglijst' },
  { value: 'social-media', label: 'Social Media' },
  { value: 'account', label: 'Account' },
  { value: 'credits', label: 'Credits' }
];

export function AdminKnowledgeBase() {
  const [articles, setArticles] = useState<KnowledgeArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<KnowledgeArticle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isReindexing, setIsReindexing] = useState(false);
  const [reindexingArticleId, setReindexingArticleId] = useState<string | null>(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const { data: articlesData, error: articlesError } = await supabase
        .from('chat_knowledge_base')
        .select('*')
        .order('created_at', { ascending: false });

      if (articlesError) throw articlesError;

      // Fetch chunk counts for all articles
      const articlesWithChunkCounts = await Promise.all(
        (articlesData || []).map(async (article) => {
          const { count } = await supabase
            .from('chat_knowledge_chunks')
            .select('*', { count: 'exact', head: true })
            .eq('knowledge_id', article.id);

          return {
            ...article,
            chunk_count: count || 0
          };
        })
      );

      setArticles(articlesWithChunkCounts);
    } catch (err: any) {
      console.error('Error fetching articles:', err);
      setError('Fout bij ophalen van artikelen');
    } finally {
      setLoading(false);
    }
  };

  const openCreateEditor = () => {
    setEditingArticle(null);
    setIsEditorOpen(true);
  };

  const openEditEditor = (article: KnowledgeArticle) => {
    setEditingArticle(article);
    setIsEditorOpen(true);
  };

  const handleSaveArticle = async () => {
    setSuccess('Artikel opgeslagen');
    setIsEditorOpen(false);
    fetchArticles();
  };

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`Weet je zeker dat je "${title}" wilt verwijderen?`)) return;

    try {
      const { error } = await supabase
        .from('chat_knowledge_base')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setSuccess('Artikel verwijderd');
      fetchArticles();
    } catch (err: any) {
      console.error('Error deleting article:', err);
      setError('Fout bij verwijderen van artikel');
    }
  };

  const togglePublished = async (id: string, currentState: boolean) => {
    try {
      const { error } = await supabase
        .from('chat_knowledge_base')
        .update({ is_published: !currentState })
        .eq('id', id);

      if (error) throw error;

      setSuccess(currentState ? 'Artikel verborgen' : 'Artikel gepubliceerd');
      fetchArticles();
    } catch (err: any) {
      console.error('Error toggling published state:', err);
      setError('Fout bij wijzigen publicatiestatus');
    }
  };

  const reindexArticle = async (knowledgeId: string) => {
    setReindexingArticleId(knowledgeId);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Niet ingelogd');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-reindex-knowledge`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ knowledge_id: knowledgeId })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Reindexering mislukt');
      }

      const result = await response.json();

      if (result.results && result.results[0]) {
        const articleResult = result.results[0];
        if (articleResult.success) {
          setSuccess(
            `Artikel geherindexeerd: ${articleResult.chunks_created} chunks, ${articleResult.embeddings_generated} embeddings`
          );
        } else {
          setError(`Reindexering mislukt: ${articleResult.error}`);
        }
      }

      fetchArticles();
    } catch (err: any) {
      console.error('Error reindexing article:', err);
      setError(err.message || 'Fout bij herindexeren van artikel');
    } finally {
      setReindexingArticleId(null);
    }
  };

  const reindexAllArticles = async () => {
    if (!confirm('Weet je zeker dat je alle gepubliceerde artikelen wilt herindexeren? Dit kan even duren.')) {
      return;
    }

    setIsReindexing(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Niet ingelogd');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-reindex-knowledge`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({})
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Reindexering mislukt');
      }

      const result = await response.json();

      if (result.summary) {
        setSuccess(
          `${result.summary.successful} van ${result.summary.total_articles} artikelen geherindexeerd. ` +
          `Totaal: ${result.summary.total_chunks} chunks, ${result.summary.total_embeddings} embeddings.`
        );
      } else {
        setSuccess('Alle artikelen geherindexeerd');
      }

      fetchArticles();
    } catch (err: any) {
      console.error('Error reindexing all articles:', err);
      setError(err.message || 'Fout bij herindexeren van artikelen');
    } finally {
      setIsReindexing(false);
    }
  };


  const filteredArticles = articles.filter(article => {
    const matchesSearch =
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory = !selectedCategory || article.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const getCategoryLabel = (value: string) => {
    return CATEGORIES.find(cat => cat.value === value)?.label || value;
  };

  const stats = {
    total: articles.length,
    published: articles.filter(a => a.is_published).length,
    indexed: articles.filter(a => (a.chunk_count || 0) > 0).length,
    byCategory: CATEGORIES.map(cat => ({
      ...cat,
      count: articles.filter(a => a.category === cat.value).length
    }))
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Kennisbank</h1>
            <p className="text-gray-600 mt-1">
              Beheer help-artikelen voor gebruikers en chatbot
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={reindexAllArticles}
              disabled={isReindexing}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isReindexing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Herindexeer alle artikelen
            </button>
            <button
              onClick={openCreateEditor}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nieuw artikel
            </button>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Bot className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-medium text-blue-900 mb-1">Automatische chatbot indexering</h3>
            <p className="text-sm text-blue-700">
              Alle artikelen worden automatisch geïndexeerd voor de chatbot wanneer ze worden opgeslagen.
              De chatbot kan hierdoor vragen van gebruikers beantwoorden op basis van deze kennisbank artikelen.
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-700">
            <X className="w-5 h-5" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2 text-green-700">
            <Check className="w-5 h-5" />
            {success}
            <button onClick={() => setSuccess(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-1">
              <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              <BookOpen className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-sm font-medium text-gray-600">Totaal artikelen</p>
          </div>
          <div className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-1">
              <p className="text-3xl font-bold text-green-600">{stats.published}</p>
              <Eye className="w-8 h-8 text-green-200" />
            </div>
            <p className="text-sm font-medium text-gray-600">Gepubliceerd</p>
          </div>
          <div className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-1">
              <p className="text-3xl font-bold text-emerald-600">{stats.indexed}</p>
              <Bot className="w-8 h-8 text-emerald-200" />
            </div>
            <p className="text-sm font-medium text-gray-600">Geïndexeerd</p>
          </div>
          <div className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-1">
              <p className="text-3xl font-bold text-blue-600">
                {articles.reduce((sum, a) => sum + (a.view_count || 0), 0)}
              </p>
              <Eye className="w-8 h-8 text-blue-200" />
            </div>
            <p className="text-sm font-medium text-gray-600">Totale views</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Zoek artikelen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedCategory('')}
                className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                  selectedCategory === ''
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Alle ({stats.total})
              </button>
              {stats.byCategory.map(cat => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                    selectedCategory === cat.value
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat.label} ({cat.count})
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
              <p className="text-gray-500 mt-2">Laden...</p>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="p-12 text-center">
              <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Geen artikelen gevonden</p>
              {searchQuery === '' && selectedCategory === '' && (
                <button
                  onClick={openCreateEditor}
                  className="mt-4 text-blue-600 hover:text-blue-700"
                >
                  Voeg je eerste artikel toe
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredArticles.map(article => {
                const isIndexed = (article.chunk_count || 0) > 0;

                return (
                  <div
                    key={article.id}
                    className={`p-5 hover:bg-gray-50 transition-colors ${
                      !article.is_published ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900">{article.title}</h3>
                          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-md">
                            {getCategoryLabel(article.category)}
                          </span>
                          {!article.is_published && (
                            <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-md">
                              Concept
                            </span>
                          )}
                          {article.video_embed && (
                            <span className="px-2 py-1 text-xs font-medium bg-pink-100 text-pink-700 rounded-md">
                              Video
                            </span>
                          )}
                          {isIndexed && (
                            <span className="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-md flex items-center gap-1">
                              <Bot className="w-3 h-3" />
                              Chatbot: {article.chunk_count} chunks
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3.5 h-3.5" />
                            {article.view_count || 0} views
                          </span>
                          <span className="text-gray-400">ID: {article.id.slice(0, 8)}...</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => reindexArticle(article.id)}
                          disabled={reindexingArticleId === article.id}
                          className="p-2 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Herindexeer artikel"
                        >
                          {reindexingArticleId === article.id ? (
                            <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
                          ) : (
                            <RefreshCw className="w-5 h-5 text-emerald-600" />
                          )}
                        </button>
                        <button
                          onClick={() => togglePublished(article.id, article.is_published)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title={article.is_published ? 'Verbergen' : 'Publiceren'}
                        >
                          {article.is_published ? (
                            <Eye className="w-5 h-5 text-green-600" />
                          ) : (
                            <EyeOff className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        <button
                          onClick={() => openEditEditor(article)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Bewerken"
                        >
                          <Pencil className="w-5 h-5 text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleDelete(article.id, article.title)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Verwijderen"
                        >
                          <Trash2 className="w-5 h-5 text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {isEditorOpen && (
        <KnowledgeArticleEditor
          article={editingArticle}
          onClose={() => setIsEditorOpen(false)}
          onSave={handleSaveArticle}
        />
      )}
    </AdminLayout>
  );
}
